import React, { useState, useEffect } from 'react';
import { Dropdown, Avatar, List, message, Badge } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import {
    fetchNotifications,
    markNotificationAsReadThunk,
    markAllNotificationsAsReadThunk
} from '../../redux/notifications/actions';
import { secureGetItem } from '../../data/utils';
import styles from '../../styles/Dashboard/Dashboard.module.css';
import { statusTokens, primaryShades } from '../../theme/theme';

/**
 * Componente per il dropdown delle notifiche nell'header
 */
const NotificationsDropdown = ({ badgeBackgroundColor, badgeIconColor }) => {
    const [notificationDropdownVisible, setNotificationDropdownVisible] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const email = secureGetItem("email");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Selettori Redux per le notifiche
    const notifications = useSelector(state => state.notifications.notifications);
    const totalUnread = useSelector(state => state.notifications.totalUnread);
    const notificationsSummary = useSelector(state => state.notifications.notificationsSummary);

    // Recupera le notifiche da Redux
    const fetchNotificationsData = () => {
        if (email) {
            dispatch(fetchNotifications(email));
        }
    };

    // Carica le notifiche all'avvio del componente
    useEffect(() => {
        fetchNotificationsData();
    }, [email, dispatch]);

    // Le notifiche si sincronizzano automaticamente tramite Redux

    // Funzione per raggruppare le notifiche per tipo (solo non lette, limitate a 3 per gruppo)
    const getGroupedNotifications = () => {
        const grouped = {};

        // Filtra solo le notifiche non lette
        const unreadNotifications = notifications.filter(notification => !notification.read);

        unreadNotifications.forEach(notification => {
            const type = notification.type;
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(notification);
        });

        // Limita ogni gruppo a 3 elementi
        Object.keys(grouped).forEach(type => {
            grouped[type] = grouped[type].slice(0, 3);
        });

        return grouped;
    };

    // Aggiorna lo stato dei gruppi espansi quando cambiano le notifiche
    useEffect(() => {
        const groupedNotifications = getGroupedNotifications();

        setCollapsedGroups(prevCollapsed => {
            const newCollapsedGroups = {};
            Object.keys(groupedNotifications).forEach(type => {
                // Se l'utente ha già fatto una scelta manuale, mantienila
                if (prevCollapsed.hasOwnProperty(type)) {
                    newCollapsedGroups[type] = prevCollapsed[type];
                } else {
                    // Solo per i nuovi gruppi, applica la logica automatica (espandi se ci sono notifiche non lette)
                    const hasUnreadNotifications = groupedNotifications[type].some(notif => !notif.read);
                    newCollapsedGroups[type] = !hasUnreadNotifications;
                }
            });
            return newCollapsedGroups;
        });
    }, [notifications]);

    // Funzione per segnare tutte le notifiche come lette
    const markAllAsRead = async () => {
        if (!email || notifications.length === 0) return;

        try {
            await dispatch(markAllNotificationsAsReadThunk(notifications));
            message.success('All notifications marked as read');
        } catch (error) {
            message.error('Failed to mark notifications as read');
        }
    };

    // Formatta la data per le notifiche
    const formatNotificationDate = (date) => {
        if (!date) return "";

        const dateObj = new Date(date);
        const now = new Date();
        const diff = now - dateObj;
        const diffMinutes = Math.floor(diff / (1000 * 60));
        const diffHours = Math.floor(diff / (1000 * 60 * 60));
        const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (diffMinutes < 60) {
            return `${diffMinutes} min ago`;
        } else if (diffHours < 24) {
            return `${diffHours} h ago`;
        } else if (diffDays < 7) {
            return `${diffDays} d ago`;
        } else {
            return dateObj.toLocaleDateString('en-US');
        }
    };

    // Ottieni colore e icona per il tipo di notifica
    const getNotificationStyleInfo = (type) => {
        switch (type) {
            case "Variant validated":
                return {
                    color: statusTokens.success,
                    icon: 'V',
                    title: 'Variant Validated'
                };
            case "Variant declined":
                return {
                    color: statusTokens.error,
                    icon: 'X',
                    title: 'Variant Declined'
                };
            case "Variant assigned":
                return {
                    color: "#1890ff",
                    icon: 'A',
                    title: 'Variant Assigned'
                };
            case "Order - Add 3d assets to catalog":
                return {
                    color: "#722ed1",
                    icon: 'O',
                    title: 'My Catalog'
                };
            default:
                return {
                    color: statusTokens.info,
                    icon: 'N',
                    title: 'Notification'
                };
        }
    };

    // Gestisce il click sulla notifica
    const handleNotificationClick = async (notification) => {
        setNotificationDropdownVisible(false);

        // NON segna la notifica come letta - sarà fatto nella pagina di destinazione
        // Naviga alla pagina delle notifiche con stato per aprire la sezione corretta
        navigate("/framevalidation/notifications", {
            state: { openGroup: notification.type }
        });
    };

    // Funzione per gestire l'espansione/compressione di un gruppo
    const toggleGroupCollapse = (e, groupType) => {
        e.stopPropagation(); // Impedisce la chiusura del dropdown
        setCollapsedGroups(prev => ({
            ...prev,
            [groupType]: !prev[groupType]
        }));
    };

    // Badge per l'header
    const HeaderBadge = ({ count }) => {
        if (!count || count <= 0) return null;

        return (
            <div
                className={styles["badge-header"]}
                style={{
                    backgroundColor: statusTokens.error,
                    color: "#fff"
                }}
            >
                {count > 10 ? '10+' : count}
            </div>
        );
    };

    // Renderizza le notifiche raggruppate
    const renderGroupedNotifications = () => {
        const groupedNotifications = getGroupedNotifications();

        if (Object.keys(groupedNotifications).length === 0) {
            return (
                <div style={{ padding: '1.25rem', textAlign: 'center', color: primaryShades.primary300 }}>
                    No unread notifications
                </div>
            );
        }

        return Object.keys(groupedNotifications).map((type, typeIndex) => {
            const notifs = groupedNotifications[type];
            const { color, icon, title } = getNotificationStyleInfo(type);

            if (notifs.length === 0) return null;

            // Usa il conteggio dalle summary Redux per questo tipo
            const unreadCount = notificationsSummary.grouped[type]?.unread || 0;
            const isCollapsed = collapsedGroups[type] || false;

            return (
                <div key={`group-${typeIndex}`} style={{
                    marginBottom: '0.75rem',
                    backgroundColor: isCollapsed ? `${color}10` : 'transparent',
                    borderRadius: '0.25rem',
                    overflow: 'hidden'
                }}>
                    <div
                        style={{
                            padding: '0.625rem 1rem',
                            backgroundColor: `${color}20`,
                            borderLeft: `0.1875rem solid ${color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            border: `0.0625rem solid ${color}30`
                        }}
                        onClick={(e) => toggleGroupCollapse(e, type)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar shape="circle" style={{
                                backgroundColor: color,
                                marginRight: '0.625rem',
                                width: '1.5rem',
                                height: '1.5rem',
                                lineHeight: '1.5rem',
                                fontSize: '0.875rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold'
                            }}>
                                {icon}
                            </Avatar>
                            <span style={{ fontWeight: 'bold' }}>{title}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {unreadCount > 0 && (
                                <Badge count={unreadCount} style={{ backgroundColor: color }} />
                            )}
                            {isCollapsed ? (
                                <CaretDownOutlined style={{ fontSize: '0.875rem', color: '#666' }} />
                            ) : (
                                <CaretUpOutlined style={{ fontSize: '0.875rem', color: '#666' }} />
                            )}
                        </div>
                    </div>
                    {!isCollapsed && (
                        <List
                            dataSource={notifs}
                            renderItem={(item) => (
                                <List.Item
                                    onClick={() => handleNotificationClick(item)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '0.75rem 1rem 0.75rem 2.8125rem',
                                        backgroundColor: item.read ? 'transparent' : `${color}15`,
                                        borderBottom: '0.0625rem solid #f0f0f0',
                                        position: 'relative'
                                    }}
                                >
                                    {!item.read && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: '0',
                                                top: '0',
                                                bottom: '0',
                                                width: '0.1875rem',
                                                backgroundColor: color,
                                            }}
                                        />
                                    )}
                                    <div style={{ width: '100%' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.3125rem'
                                        }}>
                                            <div style={{
                                                fontWeight: item.read ? 'normal' : 'bold',
                                                fontSize: '0.875rem'
                                            }}>
                                                {item.type === "Order - Add 3d assets to catalog" ? (item.nameCatalog || "N/A") : (item.nameVariant || "N/A")}
                                                {!item.read && (
                                                    <span style={{
                                                        marginLeft: '0.5rem',
                                                        fontSize: '0.6875rem',
                                                        padding: '0.0625rem 0.375rem',
                                                        borderRadius: '0.625rem',
                                                        backgroundColor: color,
                                                        color: '#fff',
                                                        fontWeight: 'normal',
                                                        verticalAlign: 'middle'
                                                    }}>
                                                        NEW
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: item.read ? '#999' : '#555',
                                                backgroundColor: item.read ? `${color}10` : `${color}20`,
                                                padding: '0.125rem 0.5rem',
                                                borderRadius: '0.25rem',
                                                fontWeight: item.read ? 'normal' : 'bold'
                                            }}>
                                                {formatNotificationDate(item.date)}
                                            </div>
                                        </div>
                                        <div style={{
                                            fontSize: '0.8125rem',
                                            color: item.read ? '#666' : '#444',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.125rem'
                                        }}>
                                            {item.message && (
                                                <div>{item.message}</div>
                                            )}
                                            {item.type === "Order - Add 3d assets to catalog" ? (
                                                <>
                                                    {item.numberAssets && (
                                                        <div>Assets: <span style={{ color: item.read ? '#555' : '#444' }}>{item.numberAssets}</span></div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {item.frameId && (
                                                        <div>Frame ID: <span style={{ color: item.read ? '#555' : '#444' }}>{item.frameId}</span></div>
                                                    )}
                                                    {item.DB && (
                                                        <div>DB: <span style={{ color: item.read ? '#555' : '#444' }}>{item.DB}</span></div>
                                                    )}
                                                </>
                                            )}
                                            {!item.message && !item.frameId && !item.DB && !item.nameCatalog && (
                                                <div>
                                                    {type === "Variant validated"
                                                        ? "Variant awaiting your approval"
                                                        : type === "Variant declined"
                                                            ? "The variant has been rejected"
                                                            : type === "Variant assigned"
                                                                ? "The variant has been assigned"
                                                                : type === "Order - Add 3d assets to catalog"
                                                                    ? "Order processing completed"
                                                                    : "General notification"}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                            style={{
                                background: 'white',
                                borderLeft: `0.0625rem solid ${color}40`,
                                borderRight: `0.0625rem solid ${color}40`,
                                borderBottom: `0.0625rem solid ${color}40`,
                                borderTop: 'none'
                            }}
                            size="small"
                        />
                    )}
                </div>
            );
        });
    };

    return (
        <Dropdown
            menu={{
                items: [
                    {
                        key: 'notificationHeader',
                        label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 1rem', borderBottom: '0.0625rem solid #e8e8e8' }}>
                                <span style={{ fontWeight: 'bold' }}>Notifications</span>
                                <div style={{ display: 'flex', gap: '0.625rem' }}>
                                    {totalUnread > 0 && (
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAllAsRead();
                                            }}
                                            style={{ cursor: 'pointer', color: 'blue' }}
                                        >
                                            Mark all as read
                                        </span>
                                    )}
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            fetchNotificationsData();
                                        }}
                                        style={{ cursor: 'pointer', color: 'black' }}
                                    >
                                        Refresh
                                    </span>
                                </div>
                            </div>
                        ),
                        style: { padding: 0 }
                    },
                    {
                        key: 'notificationsContainer',
                        label: (
                            <div style={{ maxHeight: '28.125rem', overflow: 'auto' }}>
                                {renderGroupedNotifications()}
                            </div>
                        ),
                        style: { padding: 0 }
                    },
                    {
                        key: 'viewAll',
                        label: (
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '0.625rem',
                                    borderTop: '0.0625rem solid #e8e8e8',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setNotificationDropdownVisible(false);
                                    navigate("/framevalidation/notifications");
                                }}
                            >
                                View all
                            </div>
                        ),
                        style: { padding: 0 }
                    }
                ],
                style: {
                    width: '21.875rem',
                    maxHeight: '37.5rem',
                    overflow: 'hidden'
                }
            }}
            trigger={['click']}
            open={notificationDropdownVisible}
            onOpenChange={(visible) => {
                setNotificationDropdownVisible(visible);
            }}
            placement="bottomRight"
            arrow
        >
            <div
                style={{
                    marginRight: "clamp(0.5rem, 1.5vw, 1rem)",
                    width: "clamp(1.5rem, 2vw, 1.875rem)",
                    height: "clamp(1.5rem, 2vw, 1.875rem)",
                    backgroundColor: badgeBackgroundColor,
                    borderRadius: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer"
                }}>
                <NotificationsIcon
                    style={{
                        fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
                        color: badgeIconColor,
                    }}
                />
                <HeaderBadge count={totalUnread} />
            </div>
        </Dropdown>
    );
}

export default NotificationsDropdown; 